import { useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { useArticlesByCategory, useCategoryBySlug, usePublishedArticles } from "@/hooks/usePublicArticles";
import type { Article } from "@/mocks/articles";
import AdSlotDisplay from "@/components/feature/AdSlotDisplay";

const ARTICLES_PER_PAGE = 8;

function ArticleCard({ article, color }: { article: Article; color: string }) {
  return (
    <Link to={`/artikel/${article.slug}`} className="group block bg-white rounded-xl overflow-hidden border border-hj-gray-border hover:border-hj-gray-border/80 transition-all">
      <div className="relative overflow-hidden aspect-[16/10]">
        <img src={article.imageUrl} alt={article.imageAlt || article.title} loading="lazy" width={400} height={250} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        {article.isBreaking && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold text-white bg-hj-red rounded uppercase tracking-wide">Breaking</span>
        )}
      </div>
      <div className="p-3 md:p-4">
        <h3 className="text-sm md:text-base font-bold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">{article.title}</h3>
        {article.subtitle && <p className="text-xs text-hj-gray mt-1 line-clamp-1">{article.subtitle}</p>}
        <div className="flex items-center justify-between mt-2 text-[11px] md:text-xs text-hj-muted font-body">
          <span className="font-medium text-hj-dark truncate max-w-[100px] md:max-w-[120px]">{article.author}</span>
          <div className="flex items-center gap-2 shrink-0">
            <span>{new Date(article.publishedAt).toLocaleDateString("de-CH")}</span>
            <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function ArticleRow({ article }: { article: Article }) {
  return (
    <Link to={`/artikel/${article.slug}`} className="group flex gap-3 p-2 md:p-3 rounded-lg hover:bg-hj-gray-light transition-colors">
      <div className="w-14 h-11 md:w-16 md:h-14 shrink-0 overflow-hidden rounded-md" style={{ minWidth: "56px" }}>
        <img src={article.imageUrl} alt={article.imageAlt || article.title} loading="lazy" width={64} height={56} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">{article.title}</h4>
        <p className="text-[11px] text-hj-muted mt-0.5 font-body">{new Date(article.publishedAt).toLocaleDateString("de-CH")}</p>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);

  // Derive slug from route param or pathname (e.g. /schweiz -> schweiz)
  const slug = paramSlug || location.pathname.replace(/^\//, "").replace(/\/$/, "");

  const { category, loading: catLoading } = useCategoryBySlug(slug);
  const { articles: catArticles, loading: artLoading } = useArticlesByCategory(slug);
  const { articles: allArticles } = usePublishedArticles(20);

  const loading = catLoading || artLoading;

  if (!loading && !category) {
    return (
      <div className="min-h-screen bg-white pt-[100px] flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-hj-dark mb-2 font-heading">Kategorie nicht gefunden</h1>
          <p className="text-hj-gray mb-4 font-body">Die angeforderte Kategorie existiert nicht.</p>
          <Link to="/" className="text-hj-red hover:underline font-body">Zurück zur Startseite</Link>
        </div>
      </div>
    );
  }

  const color = category?.color || "#8B0000";
  const featuredArticle = catArticles.find((a) => a.isFeatured) || catArticles[0];
  const remainingArticles = catArticles.filter((a) => a.id !== featuredArticle?.id);
  const totalPages = Math.ceil(remainingArticles.length / ARTICLES_PER_PAGE);
  const paginatedArticles = remainingArticles.slice((currentPage - 1) * ARTICLES_PER_PAGE, currentPage * ARTICLES_PER_PAGE);

  const latestNews = allArticles.filter((a) => a.categorySlug !== slug).slice(0, 6);
  const trending = [...catArticles].sort((a, b) => b.views - a.views).slice(0, 5);

  const seoTitle = category?.seoTitle || `${category?.name || slug} – Helvetik Journal`;
  const seoDesc = category?.seoDescription || category?.description || "";

  return (
    <div className="min-h-screen bg-white pt-[80px] md:pt-[130px]">
      <title>{seoTitle}</title>
      <meta name="description" content={seoDesc} />
      <link rel="canonical" href={`https://helvetik-journal.ch/${slug}`} />

      {/* Loading state */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {!loading && category && (
        <>
          {/* Category Header — compact on mobile */}
          <header className="relative overflow-hidden">
            {category.imageUrl ? (
              <div className="relative h-24 sm:h-28 md:h-64">
                <img src={category.imageUrl} alt={category.name} className="w-full h-full object-cover object-top" />
                <div className="absolute inset-0" style={{ backgroundColor: color + "cc" }}></div>
                <div className="absolute inset-0 flex flex-col justify-end px-3 md:px-8 pb-2 md:pb-8">
                  <div className="max-w-7xl mx-auto w-full">
                    <nav className="flex items-center gap-2 text-[10px] md:text-sm text-white/70 mb-1 md:mb-3 font-body">
                      <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
                      <i className="ri-arrow-right-s-line text-[10px]"></i>
                      <span className="text-white">{category.name}</span>
                    </nav>
                    <h1 className="text-lg sm:text-xl md:text-3xl lg:text-4xl font-bold text-white font-heading">{category.name}</h1>
                    <p className="text-white/80 mt-0.5 md:mt-2 max-w-2xl font-body text-xs md:text-sm hidden sm:block">{category.description}</p>
                    <div className="flex items-center gap-3 mt-1 md:mt-3 text-[10px] md:text-sm text-white/70 font-body">
                      <span>{catArticles.length} Artikel</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-3 sm:py-4 md:py-12" style={{ backgroundColor: color }}>
                <div className="max-w-7xl mx-auto px-3 md:px-6">
                  <nav className="flex items-center gap-2 text-[10px] md:text-sm text-white/70 mb-1 md:mb-3 font-body">
                    <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
                    <i className="ri-arrow-right-s-line text-[10px]"></i>
                    <span className="text-white">{category.name}</span>
                  </nav>
                  <h1 className="text-lg sm:text-xl md:text-3xl lg:text-4xl font-bold text-white font-heading">{category.name}</h1>
                  <p className="text-white/80 mt-0.5 md:mt-2 max-w-2xl font-body text-xs md:text-sm hidden sm:block">{category.description}</p>
                </div>
              </div>
            )}
          </header>

          {/* Subcategory Pills */}
          {category.subcategories.length > 0 && (
            <div className="border-b border-hj-gray-border bg-white">
              <div className="max-w-7xl mx-auto px-3 md:px-6 py-2 md:py-3 flex items-center gap-2 overflow-x-auto">
                <Link to={`/${category.slug}`} className="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors bg-hj-dark text-white">Alle</Link>
                {category.subcategories.map((sub) => (
                  <span key={sub.id} className="px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors bg-hj-gray-light text-hj-gray hover:bg-hj-gray-border cursor-pointer">{sub.name}</span>
                ))}
              </div>
            </div>
          )}

          <div className="max-w-7xl mx-auto px-3 md:px-6 py-5 md:py-12">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Main Content */}
              <main className="flex-1 min-w-0">
                {/* Mobile ad after category header */}
                <div className="md:hidden mb-4">
                  <AdSlotDisplay position="mobile_after_hero" />
                </div>

                {/* Featured Article */}
                {featuredArticle && (
                  <section className="mb-6 md:mb-10">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-1 h-4 rounded-full" style={{ backgroundColor: color }}></div>
                      <h2 className="text-xs md:text-sm font-bold uppercase tracking-wider text-hj-dark font-body">Featured</h2>
                    </div>
                    <Link to={`/artikel/${featuredArticle.slug}`} className="group block bg-white rounded-xl overflow-hidden border border-hj-gray-border">
                      <div className="flex flex-col md:flex-row">
                        <div className="w-full md:w-[55%] overflow-hidden aspect-[16/10] md:aspect-auto md:h-72">
                          <img src={featuredArticle.imageUrl} alt={featuredArticle.imageAlt || featuredArticle.title} loading="eager" width={600} height={288} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                        </div>
                        <div className="flex-1 p-4 md:p-7 flex flex-col justify-center">
                          <span className="inline-block px-2.5 py-1 text-xs font-bold text-white rounded-md uppercase tracking-wide mb-2 w-fit" style={{ backgroundColor: color }}>{featuredArticle.category}</span>
                          <h2 className="text-lg md:text-2xl font-bold text-hj-dark leading-tight group-hover:text-hj-red transition-colors font-heading">{featuredArticle.title}</h2>
                          {featuredArticle.subtitle && <p className="text-sm text-hj-gray mt-2 line-clamp-2">{featuredArticle.subtitle}</p>}
                          <p className="text-sm text-hj-gray mt-2 line-clamp-3 font-body">{featuredArticle.excerpt}</p>
                          <div className="flex items-center gap-3 mt-3 text-xs text-hj-muted font-body">
                            <span className="font-medium text-hj-dark">{featuredArticle.author}</span>
                            <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
                            <span>{new Date(featuredArticle.publishedAt).toLocaleDateString("de-CH", { day: "numeric", month: "long", year: "numeric" })}</span>
                            <span className="w-1 h-1 rounded-full bg-hj-muted"></span>
                            <span>{featuredArticle.readTime}</span>
                          </div>
                          <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium transition-colors w-fit" style={{ color }}>
                            Weiterlesen <i className="ri-arrow-right-line text-xs"></i>
                          </span>
                        </div>
                      </div>
                    </Link>
                  </section>
                )}

                {/* Articles Grid */}
                {paginatedArticles.length > 0 && (
                  <section>
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-1 h-4 rounded-full" style={{ backgroundColor: color }}></div>
                      <h2 className="text-xs md:text-sm font-bold uppercase tracking-wider text-hj-dark font-body">Neueste Artikel</h2>
                      <span className="text-[11px] text-hj-gray ml-1">({catArticles.length} gesamt)</span>
                    </div>
                    {/* First 2 cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5">
                      {paginatedArticles.slice(0, 2).map((article) => (
                        <ArticleCard key={article.id} article={article} color={color} />
                      ))}
                    </div>
                    {/* Mobile ad between card rows */}
                    {paginatedArticles.length > 2 && (
                      <div className="md:hidden my-3">
                        <AdSlotDisplay position="mobile_between_cards_cat" />
                      </div>
                    )}
                    {/* Remaining cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-5 mt-3 md:mt-0">
                      {paginatedArticles.slice(2).map((article) => (
                        <ArticleCard key={article.id} article={article} color={color} />
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 mt-8">
                        <button onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1} className="w-9 h-9 flex items-center justify-center rounded-md border border-hj-gray-border hover:bg-hj-gray-light transition-colors disabled:opacity-40">
                          <i className="ri-arrow-left-s-line"></i>
                        </button>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <button key={page} onClick={() => setCurrentPage(page)} className={`w-9 h-9 flex items-center justify-center rounded-md text-sm font-medium transition-colors ${currentPage === page ? "text-white" : "border border-hj-gray-border hover:bg-hj-gray-light text-hj-dark"}`} style={currentPage === page ? { backgroundColor: color } : {}}>
                            {page}
                          </button>
                        ))}
                        <button onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="w-9 h-9 flex items-center justify-center rounded-md border border-hj-gray-border hover:bg-hj-gray-light transition-colors disabled:opacity-40">
                          <i className="ri-arrow-right-s-line"></i>
                        </button>
                      </div>
                    )}
                  </section>
                )}

                {catArticles.length === 0 && (
                  <div className="text-center py-12">
                    <i className="ri-article-line text-4xl text-hj-gray-border mb-3"></i>
                    <p className="text-hj-gray font-body text-base">Noch keine Artikel in dieser Kategorie.</p>
                    <Link to="/" className="text-hj-red hover:underline mt-3 inline-block font-body text-sm">Zurück zur Startseite</Link>
                  </div>
                )}

                {/* Mobile ad before footer */}
                <div className="md:hidden mt-4">
                  <AdSlotDisplay position="mobile_before_footer" />
                </div>
              </main>

              {/* Sidebar */}
              <aside className="w-full lg:w-80 shrink-0 space-y-6">
                <AdSlotDisplay position="category_page" />
                {trending.length > 0 && (
                  <div className="bg-white rounded-xl border border-hj-gray-border p-4">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-hj-dark mb-3 flex items-center gap-2 font-body">
                      <i className="ri-fire-line" style={{ color }}></i>Meistgelesen
                    </h3>
                    <div className="space-y-1">
                      {trending.map((article, i) => (
                        <div key={article.id} className="flex gap-3 items-start py-2">
                          <span className="text-xl font-bold shrink-0 w-6 font-heading" style={{ color: color + "40" }}>{i + 1}</span>
                          <Link to={`/artikel/${article.slug}`} className="group flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-hj-dark leading-snug group-hover:text-hj-red transition-colors line-clamp-2 font-heading">{article.title}</h4>
                            <span className="text-xs text-hj-muted mt-0.5 block font-body">{article.views.toLocaleString("de-CH")} Aufrufe</span>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="bg-white rounded-xl border border-hj-gray-border p-4">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-hj-dark mb-3 font-body">Neueste Nachrichten</h3>
                  <div className="space-y-1">
                    {latestNews.map((article) => (
                      <ArticleRow key={article.id} article={article} />
                    ))}
                  </div>
                </div>
                <AdSlotDisplay position="sidebar_top" />
              </aside>
            </div>
          </div>
        </>
      )}
    </div>
  );
}